// Intelligent link normalization (2026-02) — a single, small, idempotent helper used at BOTH
// render time (here — this is what actually fixes ~190 legacy Freelancer records' bare
// handles/scheme-less URLs with NO data migration) and save time (the backend's mirror-image
// `backend/kernel/url_utils.py` — keep the two in lockstep if either changes). Every place a
// link/handle field is rendered anywhere in this app should go through this before being used
// as an `href`.
//
// Security first: only ever allows http/https/mailto/tel schemes — a `javascript:`/`data:`
// value is REJECTED (returns "" — the caller must never render that as a clickable href).

const ALLOWED_SCHEMES = ["http:", "https:", "mailto:", "tel:"];
const SCHEME_RE = /^([a-zA-Z][a-zA-Z0-9+.-]*):/;
const DOMAIN_RE = /^[\w-]+(\.[\w-]+)+(\/.*)?$/;

const isBare = (s) => !s.includes("/") && !s.includes(".");

/**
 * @param {string} value - raw stored/typed value (handle, scheme-less URL, or full URL).
 * @param {"instagram"|"youtube"|"vimeo"|"behance"|null} [fieldType] - omit for generic fields
 *   (Website, Work Links, external reference links, video links) — those never get a guessed
 *   platform, just an ensured `https://` when they already look like a domain.
 * @returns {string} a safe, absolute URL — or "" if empty/unrecognizable/rejected.
 */
export function normalizeUrl(value, fieldType) {
  if (!value) return "";
  let v = String(value).trim();
  if (!v) return "";

  const schemeMatch = v.match(SCHEME_RE);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase() + ":";
    if (!ALLOWED_SCHEMES.includes(scheme)) return ""; // reject javascript:, data:, etc.
    if (scheme === "http:") v = "https:" + v.slice(5);
    return v;
  }

  if (fieldType === "instagram") {
    const handle = v.startsWith("@") ? v.slice(1) : v;
    if (isBare(handle)) return `https://instagram.com/${handle}`;
    if (v.toLowerCase().includes("instagram.com")) return `https://${v.replace(/^\/+/, "")}`;
  } else if (fieldType === "behance") {
    if (isBare(v)) return `https://behance.net/${v}`;
    if (v.toLowerCase().includes("behance.net")) return `https://${v.replace(/^\/+/, "")}`;
  } else if (fieldType === "youtube") {
    if (isBare(v)) return `https://youtube.com/@${v.replace(/^@/, "")}`;
    if (v.toLowerCase().includes("youtu")) return `https://${v.replace(/^\/+/, "")}`;
  } else if (fieldType === "vimeo") {
    if (isBare(v)) return `https://vimeo.com/${v}`;
    if (v.toLowerCase().includes("vimeo.com")) return `https://${v.replace(/^\/+/, "")}`;
  }

  if (DOMAIN_RE.test(v) || /^www\./i.test(v)) return `https://${v}`;

  return v;
}

const _HOSTNAME_LABELS = [
  [/drive\.google\.com|docs\.google\.com/i, "Google Drive"],
  [/behance\.net/i, "Behance"],
  [/dropbox\.com/i, "Dropbox"],
  [/frame\.io/i, "Frame.io"],
  [/vimeo\.com/i, "Vimeo"],
  [/youtube\.com|youtu\.be/i, "YouTube"],
  [/instagram\.com/i, "Instagram"],
];

/** Collaboration UX Wave 1 (2026-09): a clean, human platform label for a non-embeddable work
 * link — "Google Drive" / "Behance" / "Dropbox" / "Frame.io" for the platforms this app
 * commonly sees, falling back to the backend's own coarser `platform` classification
 * (youtube/vimeo/instagram/website — see kernel/link_preview.py), then to a generic "Website".
 * Never the raw URL — this is the fallback used when a work sample has no freelancer-given
 * title, so it must never just print the link itself. */
export function platformLabel(url, platform) {
  if (url) {
    for (const [re, label] of _HOSTNAME_LABELS) {
      if (re.test(url)) return label;
    }
  }
  if (platform === "youtube") return "YouTube";
  if (platform === "vimeo") return "Vimeo";
  if (platform === "instagram") return "Instagram";
  return "Website";
}

/** Display-side handle extraction (e.g. show "@aravind_films", link to the full URL) — call
 * with the ALREADY-NORMALIZED url (`new URL()` needs an absolute URL to work). Falls back to
 * the raw value if it's still not a parseable URL (e.g. plain text that isn't a link at all). */
export function extractHandle(normalizedUrl, fallback) {
  if (!normalizedUrl) return fallback || "";
  try {
    const u = new URL(normalizedUrl);
    const segments = u.pathname.split("/").filter(Boolean);
    return segments.length ? `@${segments.pop()}` : u.hostname.replace(/^www\./, "");
  } catch {
    return fallback || normalizedUrl;
  }
}

const URL_IN_TEXT_RE = /https?:\/\/\S+/i;

/**
 * Intelligent Work-Link Resolver (2026-08) — turns applicant-pasted values into a clean,
 * clickable link WITHOUT any LLM, redirect-following, or network call. Layers on top of
 * normalizeUrl (reuse). Returns { href, label, resolved }:
 *   - href:     a safe absolute URL, or null when nothing valid can be extracted.
 *   - label:    descriptive text that preceded an embedded URL ("Narrative launch video"),
 *               or "" when the value was a bare URL/handle. On unresolved, the original raw.
 *   - resolved: true only when a valid URL with a real host (or mailto/tel) was produced.
 * Never fabricates a URL — if unsure, resolved:false and the raw text is preserved.
 * Mirror of backend kernel/url_utils.py::normalize_link — keep the two in lockstep.
 * @param {string} raw
 * @param {"instagram"|"youtube"|"vimeo"|"behance"|null} [fieldType]
 */
export function normalizeLink(raw, fieldType) {
  const original = raw == null ? "" : String(raw);
  if (!original.trim()) return { href: null, label: original, resolved: false };

  // 1. percent-decode (best-effort; %20 -> space, etc.)
  let decoded = original;
  try { decoded = decodeURIComponent(original); }
  catch { try { decoded = decodeURI(original); } catch { /* keep original */ } }
  decoded = decoded.trim();

  // 2. extract the first embedded http(s) URL; leading text becomes the label
  const m = decoded.match(URL_IN_TEXT_RE);
  let label = "";
  let candidate = decoded;
  if (m) {
    candidate = m[0].replace(/[)\]}>.,;'"]+$/, ""); // strip trailing punctuation artifacts
    label = decoded.slice(0, m.index).replace(/[-:|\u2013\u2014\s]+$/, "").trim();
  }

  // 3 + 4. hand off to normalizeUrl for scheme-less URLs and platform-handle expansion
  const href = normalizeUrl(candidate, fieldType);

  // 6. validate: must be a safe scheme, and http(s) must parse to a real host
  if (href && /^(https?|mailto|tel):/i.test(href)) {
    if (/^https?:/i.test(href)) {
      try {
        const u = new URL(href);
        if (!u.hostname || !u.hostname.includes(".")) throw new Error("no host");
      } catch {
        return { href: null, label: original, resolved: false };
      }
    }
    return { href, label, resolved: true };
  }
  // 7. nothing valid -> honest fallback, never fabricate
  return { href: null, label: original, resolved: false };
}
